import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../../Redux/Store';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import {
  getLoadById,
  handleResetSearchQueryError,
  updateSearchQuery,
  updateShowSearchResult,
} from '../../../Redux/Reducers/SearchSlice';
import {
  getNatNalData,
  overrideNatNalData,
  resetNatNal,
  updateIsEditSection,
} from '../../../Redux/Reducers/NatNalSlice';
import {
  dropDateOffset,
  pickupDateOffset,
  SERVER_ERROR_MESSAGES,
} from '../../../Constants/Constants';
import {
  commonDateFormat,
  convertToTimezoneFormat,
  dataBaseFriendlyDateFormat,
  dayjs,
  getTimeFormatAccordingToPreference,
  isBeforeCurrentDate,
  isBeforeCurrentDateAndTime,
} from '../../../Utils/DateUtils';
import { createNatNalSearchPayload } from '../../../Utils/App.utils';
import { EVENT, SCREEN } from '../../../Constants/AnalyticsConstants';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import CustomModal from '../../Shared/CustomModal/CustomModal';
import LoadMapContainer from '../../Shared/LoadCard/ExpandedViewContainer/LoadMapContainer/LoadMapContainer';
import NatNalEdit from './NatNalEdit/NatNalEdit';
import './NatNalComponent.scss';

interface INatNalComponentProps {}

const NatNalConfigData = [
  {
    title: 'Last Shipment Location',
    slug: 'Last_Location',
    imgSrc: '/oo/assets/icon/Last Location.svg',
  },
  {
    title: 'Next Available',
    slug: 'Next_Available',
    imgSrc: '/oo/assets/icon/Next Available.svg',
  },
];

const NatNalComponent = (props: INatNalComponentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useContext(AppContext);
  const { pathname } = useLocation();
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const appConfigData = configTableData?.FPO_APP_CONFIG;
  const { lastLocation, nextAvailableLocation, nextAvailableTime, isEditSection } = useAppSelector(
    (state: RootState) => state.NatNal,
  );
  const { showSearchResult } = useAppSelector((state: RootState) => state?.search);
  const isNatNal = pathname === '/natnal';

  const [mapModalProps, setMapModalProps] = useState<any>({
    isModalOpen: false,
  });
  const [userSelectedNextAvailableTime, setUserSelectedNextAvailableTime] = useState<any>('');
  const [selectedLocation, setSelectedLocation] = useState({
    id: '',
    value: 'Type Available Location',
  });
  const [selectedLocationTimeZone, setSelectedLocationTimeZone] = useState('');

  const handleNextAvailableTimeChange = (type: string, value: any) => {
    setUserSelectedNextAvailableTime(value);
  };

  const handleStateClear = () => {
    setSelectedLocationTimeZone('');
    setSelectedLocation({ id: '', value: 'Type Available Location' });
    setUserSelectedNextAvailableTime('');
  };

  const handleSelectedLocationChange = (target: any) => {
    setSelectedLocation(target?.value);
    setSelectedLocationTimeZone(target?.value?.timeZone || nextAvailableLocation?.timezone);
  };

  const getNatNal = useCallback(() => {
    const driverId =
      memberData?.member?.userApplicationDetails?.[0].attributes.PrimaryBookingDriverId;
    if (driverId) {
      dispatch(getNatNalData({ driverId }));
    } else {
      dispatch(resetNatNal());
    }
  }, [dispatch, memberData?.member?.userApplicationDetails]);

  useEffect(() => {
    if (location?.pathname === '/' && isEditSection) {
      dispatch(updateIsEditSection(false));
    }
  }, [dispatch, isEditSection, location?.pathname]);

  const fbLogEventPayload = {
    screen: SCREEN.NAT_NAL,
    lead: isMobile ? SCREEN.TAB_BAR : SCREEN.LEFT_MENU,
    lastLoadId: lastLocation?.shipmentId,
    nextAvailableTime: commonDateFormat(
      nextAvailableTime,
      false,
      false,
      nextAvailableLocation?.timezone || null,
    ),
    nextAvailableLocation: JSON.stringify({
      locationName: nextAvailableLocation?.locationName,
      addressLine1: nextAvailableLocation?.addressLine1,
      addressLine2: nextAvailableLocation?.addressLine2,
      city: nextAvailableLocation?.city,
      state: nextAvailableLocation?.state,
      postalCode: nextAvailableLocation?.postalCode,
    }),
  };

  const handleEdit = () => {
    if (!isNatNal) {
      navigate('/natnal', { replace: true });
    }
    isNatNal && getNatNal();
    location?.pathname === '/'
      ? dispatch(updateIsEditSection(true))
      : dispatch(updateIsEditSection(!isEditSection));
    dispatch(resetNatNal());
  };

  const getConvertedNextAvailableTime = () => {
    const arrivalTime =
      lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime?.trim() !== ''
        ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
        : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime;
    return convertToTimezoneFormat(arrivalTime, nextAvailableLocation?.timezone);
  };

  const convertedNextAvailableTime = getConvertedNextAvailableTime();

  const handleSave = () => {
    const queryPayloads = {
      driverId: memberData?.member?.userApplicationDetails?.[0].attributes.DriverId,
      nextAvailableDttm: userSelectedNextAvailableTime
        ? dayjs(userSelectedNextAvailableTime)
            .tz()
            ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference(false, true)}:ss[Z]`)
        : convertedNextAvailableTime === null ||
            isBeforeCurrentDateAndTime(convertedNextAvailableTime, nextAvailableLocation?.timezone)
          ? dayjs()
              .tz()
              ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference(false, true)}:ss[Z]`)
          : dayjs(
              lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime?.trim() !== ''
                ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
                : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime,
            )
              ?.tz(nextAvailableLocation?.timezone)
              ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss[Z]`),
      nextAvailableTimeZone: selectedLocationTimeZone || nextAvailableLocation?.timezone,
      nextAvailableLocationId: selectedLocation?.id || nextAvailableLocation?.locationId,
    };
    const callback = (res: any) => {
      if (res?.status === 'Success') {
        toast(res?.message);
        dispatch(updateIsEditSection(false));
        dispatch(resetNatNal());
      } else {
        toast(res?.message ?? SERVER_ERROR_MESSAGES?.generalMsg);
      }
    };
    dispatch(overrideNatNalData({ queryPayloads, callback }));
  };
  const handleMapModal = (isLastLocation?: boolean) => {
    let loadStopData: any = {};
    if (isLastLocation) {
      loadStopData = {
        addressLine1: lastLocation?.shipmentAssignmentStopDTO?.addressLine1,
        addressLine2: lastLocation?.shipmentAssignmentStopDTO?.addressLine2,
        city: lastLocation?.shipmentAssignmentStopDTO?.city,
        state: lastLocation?.shipmentAssignmentStopDTO?.state,
        postalCode: lastLocation?.shipmentAssignmentStopDTO?.postalCode,
        locationName: lastLocation?.shipmentAssignmentStopDTO?.locationName,
        latLang: {
          lng: lastLocation?.shipmentAssignmentStopDTO?.longitude,
          lat: lastLocation?.shipmentAssignmentStopDTO?.latitude,
        },
      };
    } else {
      loadStopData = {
        addressLine1: nextAvailableLocation?.addressLine1,
        addressLine2: nextAvailableLocation?.addressLine2,
        city: nextAvailableLocation?.city,
        state: nextAvailableLocation?.state,
        postalCode: nextAvailableLocation?.postalCode,
        locationName: nextAvailableLocation?.locationName,
        latLang: {
          lng: nextAvailableLocation?.longitude,
          lat: nextAvailableLocation?.latitude,
        },
      };
    }
    setMapModalProps({
      isModalOpen: true,
      title: 'Satellite View',
      closeModalAction: () => {
        setMapModalProps({ isModalOpen: false });
        if (!isNatNal) {
          navigate('/', { replace: true });
        } else {
          navigate('/natnal', { replace: true });
        }
      },
      contentAlignment: 'left',
      additionalCssClass: 'NatNal_Map_Modal',
      size: 'large',
      closeBtn: true,
      children: (
        <LoadMapContainer
          data={{
            loadStop: [{ ...loadStopData }],
          }}
          showZoomControls={true}
        />
      ),
    });
  };
  const handleReload = () => {
    fbLogEvent(EVENT.UE_NAT_NAL_RELOAD, fbLogEventPayload);
    const payload = createNatNalSearchPayload(
      nextAvailableLocation,
      nextAvailableTime,
      appConfigData,
    );
    dispatch(updateSearchQuery(payload));
    dispatch(handleResetSearchQueryError());
    navigate('/search', { state: { isReload: true } });
  };
  const handleLoadById = () => {
    fbLogEvent(EVENT.UE_NAT_NAL_LAST_SHIPMENT, fbLogEventPayload);
    const payload = {
      page: 0,
      managedEquipmentCapability: 'Buy Load',
      nonManagedEquipmentCapability: 'Buy Load',
      userId: memberData?.member?.upn ?? '',
      carrierId: memberData?.member?.userApplicationDetails?.[0].attributes.CommonCarrierId ?? '',
      carrierScac: memberData?.member?.carrierScac ?? null,
      carrierTier: memberData?.member?.userApplicationDetails?.[0]?.attributes?.PayType ?? '',
      userRole: memberData?.member?.userApplicationDetails?.[0]?.roles
        ?.map((item: any) => item?.roleName)
        ?.join(','),
      loadNumberAlias: lastLocation?.shipmentId,
      siteId: 'FPO',
      rateHierarchy: 'GROUP_ONLY',
    };
    dispatch(getLoadById(payload));
    !!showSearchResult && dispatch(updateShowSearchResult(false));
  };

  return (
    <>
      <CustomModal {...mapModalProps} />
      <div className={`${isNatNal ? 'NatNal_Header_Page' : 'NatNal_Header_Home'}`}>
        <p>NAT/NAL</p>
      </div>
      <div className="NatNalComponent" data-testid="NatNalComponent">
        {NatNalConfigData.map((data: any) => (
          <div key={data?.slug} className={`Component ${data?.slug}`} data-testid={data?.slug}>
            <div className="Component_Header">
              <div className="Header_section">
                <img src={data?.imgSrc} alt={data?.slug} />
                <span>{data?.title}</span>
                {data?.title === 'Next Available' && (
                  <img
                    onClick={() => {
                      fbLogEvent(EVENT.UE_NAT_NAL_REFRESH, fbLogEventPayload);
                      getNatNal();
                      dispatch(updateIsEditSection(false));
                      dispatch(resetNatNal());
                      handleStateClear();
                    }}
                    className="Next_Refresh"
                    data-testid="Next_Refresh"
                    src="/oo/assets/icon/refresh.svg"
                    alt="refresh"
                  />
                )}
              </div>
              {data?.slug === 'Next_Available' && (
                <div
                  className="Additional_section"
                  data-testid={'Additional_section'}
                  onClick={() => {
                    fbLogEvent(EVENT.UE_NAT_NAL_EDIT, fbLogEventPayload);
                    fbLogEvent(EVENT.SV_VIEW_NAT_NAL_EDIT, fbLogEventPayload);
                    handleEdit();
                  }}
                >
                  <img src="/oo/assets/icon/Edit-Orange.svg" alt={data?.slug} />
                  <span>Edit</span>
                </div>
              )}
            </div>
            <div className="Component_Content">
              {data?.slug === 'Last_Location' ? (
                <div className="Last_Location_table">
                  <div className="Last_Location_row">
                    <div className="Last_Location_column">Last Stop Details</div>
                    <div className="Last_Location_column">
                      <div
                        className="Shipment_ID_Link"
                        data-testid="Shipment_ID_Link"
                        onClick={handleLoadById}
                      >
                        {lastLocation?.shipmentId}
                      </div>
                      {lastLocation?.shipmentAssignmentStopDTO?.locationName && (
                        <div className="Shipment_Address">
                          {lastLocation?.shipmentAssignmentStopDTO?.locationName}
                          <img
                            src="/oo/assets/icon/shipment-map-icon.svg"
                            className="Map_Icon"
                            data-testid="Shipment_Address_map"
                            onClick={() => handleMapModal(true)}
                            alt="location"
                          />
                        </div>
                      )}
                      {lastLocation?.shipmentAssignmentStopDTO?.addressLine1 && (
                        <div className="Shipment_Address">
                          {lastLocation?.shipmentAssignmentStopDTO?.addressLine1}
                          {!lastLocation?.shipmentAssignmentStopDTO?.locationName && (
                            <img
                              src="/oo/assets/icon/shipment-map-icon.svg"
                              className="Map_Icon"
                              data-testid="Shipment_Address_map"
                              onClick={() => handleMapModal(true)}
                              alt="location"
                            />
                          )}
                        </div>
                      )}
                        {lastLocation?.shipmentAssignmentStopDTO?.addressLine2 && (
                          <div className='Address_Two'>{lastLocation?.shipmentAssignmentStopDTO?.addressLine2} </div>
                        )}
                      <div>
                        {lastLocation?.shipmentAssignmentStopDTO?.city &&
                          `${lastLocation?.shipmentAssignmentStopDTO?.city}, `}
                        {lastLocation?.shipmentAssignmentStopDTO?.state}{' '}
                        {lastLocation?.shipmentAssignmentStopDTO?.postalCode?.split('-')[0]}
                      </div>
                      <div>{lastLocation?.shipmentAssignmentStopDTO?.timezone}</div>
                    </div>
                  </div>
                  <div className="Last_Location_row">
                    <div className="Last_Location_column">Arrival</div>
                    <div className="Last_Location_column date">
                      {commonDateFormat(
                        lastLocation?.shipmentAssignmentStopDTO?.actualArrivalTime
                          ? lastLocation?.shipmentAssignmentStopDTO?.actualArrivalTime.trim() !== ''
                            ? lastLocation?.shipmentAssignmentStopDTO?.actualArrivalTime
                            : lastLocation?.shipmentAssignmentStopDTO?.plannedArrivalTime
                          : lastLocation?.shipmentAssignmentStopDTO?.plannedArrivalTime,
                        false,
                        true,
                      )}
                    </div>
                  </div>
                  <div className="Last_Location_row">
                    <div className="Last_Location_column">Departure</div>
                    <div className="Last_Location_column date">
                      {commonDateFormat(
                        lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
                          ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime.trim() !==
                            ''
                            ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
                            : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime
                          : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime,
                        false,
                        true,
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="Next_Available_table">
                  <div className="Next_Available_row">
                    <div className="Next_Available_column">Next Available Time</div>
                    <div className="Next_Available_column date">
                      {commonDateFormat(nextAvailableTime, false, true)}
                    </div>
                  </div>
                  <div className="Next_Available_row">
                    <div className="Next_Available_column">Next Available Location</div>
                    <div className="Next_Available_column">
                      {nextAvailableLocation?.locationName && (
                        <div className="Shipment_Address">
                          {nextAvailableLocation?.locationName}
                          <img
                            src="/oo/assets/icon/shipment-map-icon.svg"
                            className="Map_Icon"
                            data-testid="Shipment_Address_map"
                            onClick={() => handleMapModal()}
                            alt="location"
                          />
                        </div>
                      )}
                      {nextAvailableLocation?.addressLine1 && (
                        <div className="Shipment_Address">
                          {nextAvailableLocation?.addressLine1}
                          {!nextAvailableLocation?.locationName && (
                            <img
                              src="/oo/assets/icon/shipment-map-icon.svg"
                              className="Map_Icon"
                              data-testid="Shipment_Address_map"
                              onClick={() => handleMapModal()}
                              alt="location"
                            />
                          )}
                        </div>
                      )}
                      {nextAvailableLocation?.addressLine2 && (
                        <div>{nextAvailableLocation?.addressLine2} </div>
                      )}
                      <div>
                        {nextAvailableLocation?.city && nextAvailableLocation?.city + ','}
                        {nextAvailableLocation?.state && nextAvailableLocation?.state}{' '}
                        {nextAvailableLocation?.postalCode &&
                          nextAvailableLocation?.postalCode?.split('-')[0]}
                      </div>
                      <div>{nextAvailableLocation?.timezone}</div>
                      {!!(nextAvailableLocation && nextAvailableTime) && (
                        <div className="Reload" data-testid="Reload" onClick={() => handleReload()}>
                          <img src="/oo/assets/icon/reload_icon.svg" alt="reload" />
                          Reload
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {data?.slug === 'Next_Available' && isEditSection && isMobile && (
              <NatNalEdit
                handleSave={handleSave}
                userSelectedNextAvailableTime={
                  userSelectedNextAvailableTime
                    ? userSelectedNextAvailableTime
                    : lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime?.trim() !== ''
                      ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
                      : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime
                }
                handleDateChange={handleNextAvailableTimeChange}
                selectedLocation={selectedLocation?.value}
                handleSelectedLocationChange={handleSelectedLocationChange}
              />
            )}
          </div>
        ))}
      </div>
      {isEditSection && !isMobile && (
        <NatNalEdit
          handleSave={handleSave}
          userSelectedNextAvailableTime={
            userSelectedNextAvailableTime
              ? userSelectedNextAvailableTime
              : lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime?.trim() !== ''
                ? lastLocation?.shipmentAssignmentStopDTO?.actualDepartureTime
                : lastLocation?.shipmentAssignmentStopDTO?.plannedDepartureTime
          }
          handleDateChange={handleNextAvailableTimeChange}
          selectedLocation={selectedLocation?.value}
          handleSelectedLocationChange={handleSelectedLocationChange}
        />
      )}
    </>
  );
};
export default NatNalComponent;
